import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Flex, Image, Spinner, Text } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { AppBar, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import { appImages } from "../../assets/appicons";
import { getProjectByIdApi } from "../../api/finance.project.api";
import { resolveImageUrl } from "../../utils/format";
import { projectSummary } from "../../utils/financeFormat";
import RichDescription from "../../components/RichDescription";
import { EMP_BASE_PATH, EMP_API_BASE_URL } from "../../config";

const TEXT = "#08142D";
const MUTED = "#6B7280";

const InfoRow = ({ label, children }) => (
  <Flex mb="12px" align="flex-start">
    <Text w="30%" pr="12px" fontSize="14px" color={TEXT} lineHeight="22px">{label}</Text>
    <Box flex="1">{children}</Box>
  </Flex>
);
const Val = ({ children }) => (
  <Text fontSize="14px" fontWeight={600} color={TEXT} lineHeight="22px" textAlign="left">{children || "-"}</Text>
);

const ImageSlider = ({ paths }) => {
  const ref = useRef(null);
  const drag = useRef({ down: false, x: 0, scroll: 0 });
  const imgs = paths?.length ? paths : [null];
  return (
    <Flex
      ref={ref}
      mt="16px"
      gap="8px"
      overflowX="auto"
      css={{ "::-webkit-scrollbar": { display: "none" }, scrollbarWidth: "none" }}
      cursor="grab"
      onMouseDown={(e) => { drag.current = { down: true, x: e.pageX, scroll: ref.current.scrollLeft }; }}
      onMouseMove={(e) => { if (drag.current.down) ref.current.scrollLeft = drag.current.scroll - (e.pageX - drag.current.x); }}
      onMouseUp={() => { drag.current.down = false; }}
      onMouseLeave={() => { drag.current.down = false; }}
      sx={{ userSelect: "none" }}
    >
      {imgs.map((p, i) => (
        <Image
          key={i}
          src={p ? resolveImageUrl(p, EMP_API_BASE_URL) : appImages.image_project_example}
          alt=""
          flex="0 0 auto"
          w={imgs.length > 1 ? "88%" : "100%"}
          h="180px"
          objectFit="cover"
          borderRadius="8px"
          draggable={false}
        />
      ))}
    </Flex>
  );
};

const ProjectDetail = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getProjectByIdApi(id)
      .then((d) => setData(d))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  const s = projectSummary(data?.apartment);
  const paths = (data?.images || []).map((x) => x?.path).filter(Boolean);

  const pageBody = (
    <>

      <Box flex="1" overflowY="auto">
        {loading ? (
          <Flex justify="center" py="60px"><Spinner color={APP.primary200} /></Flex>
        ) : error || !data ? (
          <Flex justify="center" py="60px"><Text fontSize="14px" color={MUTED}>Không tìm thấy thông tin dự án</Text></Flex>
        ) : (
          <Box>
            <Text mx="16px" mt="12px" fontSize="18px" fontWeight={600} color={TEXT} noOfLines={2}>{data?.name || "-"}</Text>
            <Text mx="16px" mt="8px" fontSize="14px" color={MUTED} noOfLines={2}>{data?.address || "-"}</Text>

            <Box px="16px"><ImageSlider paths={paths} /></Box>

            {/* Thông tin dự án */}
            <Box px="16px" pt="16px">
              <InfoRow label="Diện tích"><Val>{s.totalArea}</Val></InfoRow>
              <InfoRow label="Số căn hộ"><Val>{s.totalApartment}</Val></InfoRow>
              <InfoRow label="Giá"><Val>{s.totalValueRange}</Val></InfoRow>
              <InfoRow label="Chủ đầu tư"><Val>{data?.investor?.name}</Val></InfoRow>
              <InfoRow label="Tỉnh/thành phố"><Val>{data?.city?.name}</Val></InfoRow>
            </Box>

            {/* Tổng quan */}
            {data?.description && (
              <Box px="16px" pt="4px" pb="16px">
                <Text fontSize="16px" fontWeight={600} color={TEXT} mb="8px">Tổng quan</Text>
                <RichDescription html={data.description} />
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Nút Booking */}
      <Box borderTop="1px solid #EEF1F6" p="16px">
        <Button
          w="100%"
          h="48px"
          bg={APP.primary200}
          color="white"
          borderRadius="8px"
          _hover={{ bg: APP.primary200, opacity: 0.92 }}
          onClick={() => navigate(`${EMP_BASE_PATH}/tai-chinh/chon-can-ho?mode=booking&projectId=${id}`)}
        >
          Booking
        </Button>
      </Box>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Chi tiết dự án" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg="white" minH="100dvh" display="flex" flexDirection="column">
      <AppBar title="Chi tiết dự án" onBack={() => navigate(`${EMP_BASE_PATH}/tai-chinh/du-an`)} />
      {pageBody}
    </Box>
  );
};

export default ProjectDetail;
